import { DEFAULT_CHATS_PER_REQUEST } from '../../constants/chatsPerRequest';
import { mapChat } from '../../mappers';
import { fetchWebChatsGet, TWebChatsGetRequest, TWebChatsGetResponse } from '../../repositories/chats/v3/web/chats/get';
import { IApplicationContext } from '../../types/applicationContext';
import { TFilterBySyncChatSchema, TStatusSyncChatSchema } from '../../types/chatList';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { ETelemetryEvents } from '../../types/telemetry';
import { getTelemetryKeyByPlatformAndHostType } from '../../utils/getTelemetryKeyByPlatformAndHostType';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { getResponseError } from '../../utils/logging/getResponseError';

export interface IFetchChatsV2ParamsRequest {
  /** timestamp последнего чата, последней страницы */
  timestamp?: number;
  /** Фильтр для синхронизации чатов **/
  filterBy?: TFilterBySyncChatSchema;
  /** Раздел чатов **/
  status?: TStatusSyncChatSchema;
  subAgentRealtyUserId?: number;
}

type TFetchChatsV2Params = {
  parameters: IFetchChatsV2ParamsRequest;
  cookies?: string;
};

const DEFAULT_ERROR_MESSAGE = 'Неизвестная ошибка при получении чатов';

export async function fetchChatsV2(
  { logger, httpApi, telemetry, config, custom: { realDevice, hostType } }: IApplicationContext,
  { cookies, parameters }: TFetchChatsV2Params,
) {
  const { timestamp, filterBy, status, subAgentRealtyUserId } = parameters;

  const limit = config.get<number>('frontend-chats.chatsPerRequest') || DEFAULT_CHATS_PER_REQUEST;

  const options: TWebChatsGetRequest = {
    limit,
    minDatetimeForSorting: timestamp ? new Date(timestamp).toISOString() : undefined,
    subAgentRealtyUserId,
    filterBy,
    status: status || 'active',
  };

  const startTime = Date.now();

  const initTelemetryKey = getTelemetryKeyByPlatformAndHostType({
    eventKey: ETelemetryEvents.ChatsGetInit,
    deviceType: realDevice,
    hostType,
  });

  telemetry.increment(initTelemetryKey);

  try {
    const { response, statusCode }: TWebChatsGetResponse = await fetchWebChatsGet({
      httpApi,
      parameters: options,
      config: {
        requestConfig: {
          headers: cookies ? [['Cookie', cookies]] : [],
          withCredentials: true,
        },
      },
    });

    if (statusCode !== 200) {
      throw getResponseError({
        domain: EExceptionDomain.ChatsGet,
        response,
        defaultMessage: DEFAULT_ERROR_MESSAGE,
        statusCode,
      });
    }

    telemetry.increment(ETelemetryEvents.ChatsGetSuccess);
    telemetry.timing(ETelemetryEvents.ChatsGetTime, Date.now() - startTime);

    return response.chats.map(mapChat) || [];
  } catch (error) {
    telemetry.increment(ETelemetryEvents.ChatsGetFail);

    const errorToLog = getGenericLikeError({
      error,
      domain: EExceptionDomain.ChatsGet,
      message: DEFAULT_ERROR_MESSAGE,
    });

    logger.error(errorToLog, {
      microserviceName: 'chats',
      pathApi: '/v3/web/chats/get/',
      parameters: JSON.stringify(options),
    });

    return null;
  }
}
